import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ProfilePageService } from '../services/profile-page.service';
import { Employee, Farm, FarmLocations, GetFarmImagesResponseType, GetUsersResponseType, Product, Review } from '../../models/types';
import { LoginService } from '../services/login.service';
import { UserInfo } from '../../models/types';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpResponse } from '@angular/common/http';
import { FullCalendarComponent } from "../components/full-calendar/full-calendar.component";
import { MapComponent } from '../components/map/map.component';
import { MapService } from '../services/map.service';
import { FarmStatisticsPageComponent } from '../farm-statistics-page/farm-statistics-page.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { 
  faMagnifyingGlass, 
  faCow,
  faStar,
  faLocationDot,
  faLocationCrosshairs, 
  faTractor,
  faEdit, 
  faTrash, 
  faUsers, 
  faUser,
  faPlus, 
  faClipboard,
  faEnvelope,
  faMessage,
  faPhone,
  faImage,
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { MarketPageService } from '../services/market-page.service';
import { StarRatingModule } from 'angular-star-rating';
import { ProductListComponent } from '../components/product-list/product-list.component';
import { RouterOutlet, RouterModule } from '@angular/router';
import { GalleryImage, GalleryItemEvent, GalleryModule } from '@daelmaak/ngx-gallery';
import { imageLocation } from '../../environments/environment.development';

@Component({
  selector: 'app-farm-profile-page',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    FullCalendarComponent, 
    MapComponent,
    FarmStatisticsPageComponent, 
    FontAwesomeModule, 
    StarRatingModule, 
    ProductListComponent, 
    RouterModule,
    RouterOutlet,
    GalleryModule,
    ],
    
  templateUrl: './farm-profile-page.component.html',
  styleUrl: './farm-profile-page.component.scss'
})
export class FarmProfilePageComponent implements OnInit{

  farm: Farm = {
    userID: 0,
    farmID: 0,
    name: '',
    address: '',
    phone: '',
    email: '',
    employees: [],
    description: '',
    products: []
  };

  vegetableImageUrl = '../../assets/images/generic/vegetable.png';
  meatImageUrl = '../../assets/images/generic/meat.png';
  dairyImageUrl = '../../assets/images/generic/dairy.png';
  otherImageUrl = '../../assets/images/generic/other.png';

  farmDefaultImageUrl = '../../assets/images/generic/farm.png';


  editingEmployeeIndex: number = -1;
  newEmployee: Employee = {
    employeeID: 0,
    name: '',
    surname: '',
    employeeDescription: ''
  };

  editingProductIndex: number = -1; 
  newProduct: Product = {
    productID: 0,
    farmID: 0,
    name: '',
    description: '',
    cost: 0,
    quantity: 0,
    type: 'ostalo',
    image: ''
  };

  farmImages: GalleryImage[] = [];

  employees: Employee[] = [];
  products: Product[] = [];
  farmLocations: FarmLocations[] = [];

  isEditMode: boolean = false;
  errorMessage: string = '';
  userId: number = 0;
  loggedInUserInfo: UserInfo | undefined;
  isLoading: boolean = false;
  responseMessage: string = '';
  isFarmEditMode: boolean = false;
  isFarmInfoVisible = false;
  showEmployeeForm: boolean = false;
  showProductForm: boolean = false;

  imageFiles: File[] = []; // Array to hold selected image files
  previewImageSrcs: string[] = []; // Array to hold preview URLs
  selectedImageIndex= -1;
  imageSelected = false;

  faMagnifyingGlass = faMagnifyingGlass;
  faCow = faCow;
  faStar = faStar;
  faLocationDot = faLocationDot;
  faLocationCrosshairs = faLocationCrosshairs;
  faTractor = faTractor;
  faEdit = faEdit;
  faTrash = faTrash;
  faUsers = faUsers;
  faUser = faUser;
  faPlus = faPlus;
  faClipboard = faClipboard;
  faEnvelope = faEnvelope;
  faPhone = faPhone;
  faMessage = faMessage;
  faImage = faImage;

  currentSection: string = 'farm';

  adminMode: boolean = false;
  showMap: boolean = true;

  constructor(
    private profilePageService: ProfilePageService,
    private loginService: LoginService,
    private router: Router,
    private mapService: MapService,
    private marketPageService: MarketPageService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef) 
    { }

    ngOnInit(): void {
      console.log('FarmProfilePage initialized.');
      
      // Retrieve farmID from URL
      const farmID = parseInt(this.route.snapshot.paramMap.get('farmID')!, 10);
      
      // Check if farmID is valid
      if (!isNaN(farmID)) {
        // FarmID is present, handle the not-logged-in user flow
        console.log('Fetching farm data for farmID:', farmID);

        this.fetchFarmDataForUser(farmID);

        this.fetchAllFarmLocations();

        this.fetchFarmImages(farmID);

        this.adminMode = false; // Not an admin in this case
      } else {
        // FarmID is not present, handle the logged-in user flow
        this.loggedInUserInfo = this.loginService.GetLoggedInUserInfo();
        if (this.loggedInUserInfo) {

          console.log('User is logged in, fetching data based on userID:', this.loggedInUserInfo.userID);

          this.fetchFarmData();

          this.fetchAllFarmLocations();

          this.adminMode = true; // Admin mode enabled
        } else {
          // Handle scenario where user is not logged in and no farmID is present
          console.error('User is not logged in and no valid farmID found.');
          window.location.href = '/'; // Redirect to home page
        }
      }
    }

  fetchFarmDataForUser(farmID: number): void {
    this.profilePageService.getFarmInfoForUser(farmID).subscribe(
      (response) => {
        this.farm = response.body!;
        console.log('Farm data loaded successfully for normal user:', this.farm);
        this.employees = this.farm.employees;
        console.log("Employees for normal user:", this.employees);
        this.products = this.assignProductImages(this.farm.products);
        console.log("Products for normal user:", this.products);

        this.fetchFarmImages(this.farm.farmID);
        this.cdr.detectChanges();
      },
      (error) => {
        this.errorMessage = 'Error loading farm data';
        console.error('Error loading farm data:', error);
      }
    );
  }

  fetchFarmData(): void {
    const loggedInUserInfo = this.loginService.GetLoggedInUserInfo();
    if (loggedInUserInfo) {
      this.profilePageService.getFarmInfo(loggedInUserInfo.userID).subscribe(
        (response) => {
          this.farm = response.body!;
          console.log('Farm data loaded successfully:', this.farm);

          this.employees = this.farm.employees;
          console.log("Employees:", this.employees);

          this.products = this.assignProductImages(this.farm.products);
          console.log("Products:", this.products);

          this.fetchFarmImages(this.farm.farmID);
        },
        (error) => {
          this.errorMessage = 'Error loading farm data';
          console.error('Error loading farm data:', error);
        }
      );
    }
  }

  fetchFarmImages(farmID: number): void {
    this.profilePageService.getFarmImages(farmID).subscribe(
      (response) => {
        console.log('Farm images loaded successfully:', response);
        this.farmImages = response.body!.images.map((image: any) => {
          return new GalleryImage(
            `${imageLocation}/static/content/farms/${farmID}/${image}`,
            `${imageLocation}/static/content/farms/${farmID}/${image}`,
          );
        });
      },
      (error) => {
        console.error('Error loading farm images:', error);
      }
    );
  }

  onThumbClick(event: GalleryItemEvent) {
    this.selectedImageIndex = event.index;
    this.imageSelected = true;
    console.log(`Selected image index: ${this.selectedImageIndex}`);
  }

  deleteSelectedImage() {
    if (this.selectedImageIndex !== null) {
      Swal.fire({
        title: 'Ali želite nadaljevati?',
        text: 'Ali želite izbrisati sliko?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Da, izbriši!',
        cancelButtonText: 'Ne, prekliči!'
      }).then((result) => {
        if (result.isConfirmed) {
          const imageToDelete = this.farmImages[this.selectedImageIndex];
          const pictureSrc = imageToDelete.src;
  
          this.profilePageService.deleteFarmImage(pictureSrc).subscribe({
            next: (response: HttpResponse<any>) => {
              console.log('Image deleted successfully:', response);
              this.farmImages.splice(this.selectedImageIndex, 1); // Remove image from the gallery
              this.selectedImageIndex = -1;
              this.imageSelected = false;
              // You might want to update the UI or use Angular's change detection instead of reloading the page
              window.location.reload();
            },
            error: (err) => {
              console.error('Failed to delete image:', err);
            }
          });
        } else {
          console.log('Image deletion cancelled');
        }
      });
    } else {
      console.log('No image selected for deletion');
    }
  }

  private assignProductImages(products: Product[]): Product[] {
    return products.map(product => {
      product.averageRating = this.calculateAverageRating(product.reviews);
      
      let defaultImageUrl;
      switch (product.type) {
        case 'zelenjava':
          defaultImageUrl = this.vegetableImageUrl;
          break;
        case 'mlečni izdelek':
          defaultImageUrl = this.dairyImageUrl;
          break;
        case 'meso':
          defaultImageUrl = this.meatImageUrl;
          break;
        default:
          defaultImageUrl = this.otherImageUrl;
          break;
      }
  
      if (!product.image) {
        product.image = defaultImageUrl;
      } else {
        product.image = `${imageLocation}/static/content/${product.productID}/${product.image}`;
      }
  
      //console.log('Product Image URL:', product.image); // Log the image URL
      return product;
    });
  }

  calculateAverageRating(reviews?: Review[]): number {
    if (reviews && reviews.length > 0) {
      const total = reviews.reduce((sum, review) => sum + review.rating, 0);
      return total / reviews.length;
    }
    return 0;
  }
  

  saveFarm() {
    this.profilePageService.updateFarmInfo(this.farm!).subscribe(
      (response) => {
        this.toggleFarmEditMode();
        this.fetchFarmData();
        console.log('Farm data saved successfully:', response);
      },
      (error) => {
        this.errorMessage = 'Error saving farm data';
        console.error('Error saving farm data:', error);
      }
    );
  }

  toggleFarmEditMode() {
    this.isFarmEditMode = !this.isFarmEditMode;
  }

  removeEmployee(index: number, employeeID: number): void {
    Swal.fire({
      title: 'Ste prepričani?',
      text: "Ali želite odstraniti tega zaposlenega?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Da, odstrani!',
      cancelButtonText: 'Prekliči'
    }).then((result) => {
      if (result.isConfirmed) {
        console.log('Removing employee:', this.employees[index].employeeID + ' from farm: ' + this.farm.farmID + ' at index: ' + index + '...');
        this.profilePageService.deleteEmployee(this.farm.farmID, employeeID).subscribe(
          (response: HttpResponse<any>) => {
            console.log('Employee removed successfully:', response);
            // Filter out the removed employee from both arrays
            this.farm.employees = this.farm.employees.filter(emp => emp.employeeID !== employeeID);
            this.employees = this.employees.filter(emp => emp.employeeID !== employeeID);
            Swal.fire(
              'Odstranjeno!',
              'Zaposleni je bil uspešno odstranjen.',
              'success'
            );
          },
          (error: any) => {
            console.error('Error removing employee:', error);
            // Handle error removing employee
            Swal.fire(
              'Napaka!',
              'Prišlo je do napake pri odstranjevanju zaposlenega.',
              'error'
            );
          }
        );
      }
    });
  }
  
  

  removeProduct(index: number, productID: number): void {
    Swal.fire({
      title: 'Ste prepričani?',
      text: "Ali želite odstraniti ta izdelek?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Da, odstrani!',
      cancelButtonText: 'Prekliči'
    }).then((result) => {
      if (result.isConfirmed) {
        console.log('Removing product:', this.products[index].productID + ' from farm: ' + this.farm.farmID + ' at index: ' + index + '...');
        this.profilePageService.deleteProduct(this.farm.farmID, productID).subscribe(
          (response: HttpResponse<any>) => {
            console.log('Product removed successfully:', response);
            // Remove product from local products array
            this.farm.products.splice(index, 1);
            this.products.splice(index, 1);
            Swal.fire(
              'Odstranjeno!',
              'Izdelek je bil uspešno odstranjen.',
              'success'
            );
          },
          (error: any) => {
            console.error('Error removing product:', error);
            // Handle error removing product
            Swal.fire(
              'Napaka!',
              'Prišlo je do napake pri odstranjevanju izdelka.',
              'error'
            );
          }
        );
      }
    });
  }
  

//Farm locations start
fetchAllFarmLocations() {
  this.mapService.GetFarmLocations().subscribe((response) => {
    this.farmLocations = this.transformResponseToFarmLocations(response);
    console.log('Farm Locations in profile-page:', this.farmLocations);
  });
}

transformResponseToFarmLocations(response: any): FarmLocations[] {
  const locationsObj = response.body.locations;
  const farmLocationsArray: FarmLocations[] = [];

  for (const key in locationsObj) {
    if (locationsObj.hasOwnProperty(key)) {
      farmLocationsArray.push(locationsObj[key]);
    }
  }

  return farmLocationsArray;
}

getTruncatedDescription(description: string, length: number): string {
  return description.length > length ? description.slice(0, length) : description;
}

openContactModal(product: Product) {
  if (this.loginService.IsUserLoggedIn()) {
    this.marketPageService.openContactModal(product);
  } else {
    Swal.fire({
      title: 'Obvezna prijava',
      text: 'Za kontakt s prodajalcem se morate prijaviti v sistem.',
      icon: 'info',
      confirmButtonText: 'OK'
    });
  }
}

openReviewModal(event: Event, product: Product) {
  event.preventDefault();
  if (this.loginService.IsUserLoggedIn()) {
    
    this.marketPageService.openReviewModal(product);
  } else {
    Swal.fire({
      title: 'Obvezna prijava',
      text: 'Za komentiranje izdelkov se morate prijaviti v sistem.',
      icon: 'info',
      confirmButtonText: 'OK'
    });
  }
}

subscribeToFarm() {
  if (this.loginService.IsUserLoggedIn()) {
    const subscriberID = this.loginService.GetLoggedInUserInfo()!.userID;

    Swal.fire({
      title: 'Obvestilo o naročnini',
      text: 'Z naročnino boste prejemali novice o dogodkih in novih izdelkih s te kmetije.',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Naroči se',
      cancelButtonText: 'Prekliči'
    }).then((result) => {
      if (result.isConfirmed) {
        this.marketPageService.subscribeToFarm(this.farm, subscriberID).subscribe(
          (response) => {
            console.log('Subscribe Response:', response);
            if (response.status === 201) {
              // Subscription added successfully
              console.log('Subscribed to farm:', this.farm);
              Swal.fire({
                title: 'Naročeni ste na kmetijo',
                text: 'Na email boste prejeli obvestila o novih izdelkih, ponudbah in dogodkih.',
                icon: 'success',
                confirmButtonText: 'OK'
              });
            } else {
              // Handle other status codes if needed
              console.error('Unexpected status:', response.status);
              Swal.fire({
                title: 'Napaka',
                text: 'Prišlo je do napake pri naročilu na kmetijo. Poskusite znova kasneje.',
                icon: 'error',
                confirmButtonText: 'OK'
              });
            }
          },
          (error) => {
            console.error('Error subscribing to farm:', error);
            if (error.status === 409) {
              Swal.fire({
                title: 'Napaka pri naročilu',
                text: 'Naročilo na to kmetijo že obstaja.',
                icon: 'error',
                confirmButtonText: 'OK'
              });
            } else {
              Swal.fire({
                title: 'Napaka',
                text: 'Prišlo je do napake pri naročilu na kmetijo. Poskusite znova kasneje.',
                icon: 'error',
                confirmButtonText: 'OK'
              });
            }
          }
        );
      }
    });
  } else {
    Swal.fire({
      title: 'Niste prijavljeni',
      text: 'Prosimo, da se prijavite, da se lahko naročite na kmetijo.',
      icon: 'info',
      confirmButtonText: 'OK'
    });
  }
}

showMapOnClick() {
  this.showMap = true;
}

hideMapOnClick() {
  this.showMap = false;
}


//Farm locations end

//Modal functions
  openUpdateEmployeeModal(employee: Employee) {
    this.profilePageService.open(employee);
  }

  openUpdateProductModal(product: Product) {
    this.profilePageService.openProduct(product);
  }

  openAddProductModal(): void {
    this.profilePageService.openProductAdd(this.farm);
  }

  openAddEmployeeModal(): void {
    this.profilePageService.openEmployeeAdd(this.farm);
  }

  openContactFarmModal(): void {
    this.profilePageService.openContactFarmModal(this.farm);
  }

  openEmployeeDetailsModal(event: Event, employee: Employee): void {
    event.preventDefault();
    this.profilePageService.openEmployeeDetailsModal(employee);
  }

  openAddFarmImagesModal(): void {
    this.profilePageService.openImageAddModal(this.farm);
  }

}
